import {
  ButtonHTMLAttributes,
  ReactNode,
  useEffect,
  useState,
  useRef,
} from "react";
import styles from "./style.module.scss";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import ChevronIcon from "assets/icons/ChevronIcon";
import BaseButton from "../Buttons/BaseButton";
interface FilterButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "selected";
  label: string;
  iconLeft?: ReactNode;
  className?: string;
  children?: ReactNode;
  selectedLabel?: string;
  itemCount?: number;
  confidenceLevel?: number;
  closeOnSelection?: boolean;
  tooltip?: string;
}

const FilterButton = ({
  variant = "default",
  label,
  iconLeft,
  className,
  children,
  selectedLabel,
  itemCount,
  confidenceLevel,
  tooltip,
  closeOnSelection = false,
}: FilterButtonProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [childStyle, setChildStyle] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });

  const showTooltip = !!tooltip && !isExpanded && variant === "default";
  const tooltipId = `dropdown-button-tooltip-${label}`;
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleSelection = () => {
    if (closeOnSelection) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update child position based on button's position
  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setChildStyle({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, [isExpanded]);

  const renderButtonLabel = () => {
    return (
      <div className={styles.contentContainer}>
        <div className={styles.labelContainer}>
          <span>
            {label}
            {selectedLabel && ":"}
          </span>
          {selectedLabel && <span>{selectedLabel}</span>}
        </div>
        {!!itemCount && (
          <div className={styles.itemCount}>
            <span>+</span>
            <span>{itemCount}</span>
          </div>
        )}
        {!!confidenceLevel && (
          <div className={styles.confidenceLevel}>{confidenceLevel}</div>
        )}
      </div>
    );
  };

  const renderChevronIcon = () => {
    return isExpanded ? (
      <ChevronIcon direction="up" dataTestId="chevron-up-icon" />
    ) : (
      <ChevronIcon direction="down" dataTestId="chevron-down-icon" />
    );
  };

  return (
    <div
      ref={buttonRef}
      className={className}
      data-tooltip-content={tooltip}
      data-tooltip-id={tooltipId}
    >
      <BaseButton
        variant={variant}
        label={renderButtonLabel()}
        iconLeft={iconLeft}
        iconRight={renderChevronIcon()}
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
      />
      {children && isExpanded && (
        <div
          style={{
            top: childStyle.top + "px",
            left: childStyle.left + "px",
            width: childStyle.width + "px",
          }}
          className={styles.childrenContainer}
          onClick={handleSelection}
        >
          {children}
        </div>
      )}
      {showTooltip && <TooltipWrapper id={tooltipId} />}
    </div>
  );
};

export default FilterButton;
